import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import {
  Box,
  Button,
  VerticalBox,
  Loader,
  Text,
  Title,
} from '../../components';
import { COLORS, device, FONTS, ROUTES } from '../../constants';
import { ActivityCard } from './partials/ActivityCard/ActivityCard';
import { IndustryCard } from './partials/IndustryCard/IndustryCard';
import { getActivityIcons } from './partials/ActivityCard/data';
import { useTranslation } from 'react-i18next';
import { getIndustryIcons } from './partials/IndustryCard/data';
import { IndustriesSlider } from './partials/IndustriesSlider/IndustriesSlider';
import { fetchHomePageData } from '../../services/home/api';
import { ApiResponse, TopProject } from '../../types/globalTypes';
import Technology from './partials/Technology/Technology';
import HeaderLogo from './partials/Header/HeaderLogo';
import About from './partials/About/About';
import AllTechnologies from './partials/AllTechnologies/AllTechnologies';
import Cta from './partials/Cta/Cta';
import Faq from './partials/Faq/Faq';
import IndustriesProgress from './partials/IndustriesSlider/IndustriesProgress';
import {
  ArrowRightSmall,
  DownIndustriesArrow,
  UpIndustriesArrow,
} from '../../assets/svg';
import { deviceHeight } from '../../constants/mediaQueries';
import { NavLink as Link } from 'react-router-dom';
import { PortfolioSlider } from './partials/Pofrtolio/PortfolioSlider';
import { PortfolioCard } from './partials/Pofrtolio/PortfolioCard';
import KeyPointsList from './partials/DevelopmentProcess/keyPointsList';
import { useQuery } from '@tanstack/react-query';
import {
  MotionDevelopmentProcess,
  MotionMainHeaderBackground,
  MotionProjectSliderCard,
  MotionScaleButton,
  MotionText,
} from '../../components/Motion';

const TEAM_FACTS_SHOWN = 6;

export const Home: React.FC = () => {
  const { t } = useTranslation();
  const [selectedProcess, setSelectedProcess] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const [domains, setDomains] = useState<any[]>([]);
  const [showAllTeamsFacts, setShowAllTeamsFacts] = useState(false);

  const handleScroll = useCallback(() => {
    setTimeout(() => {
      if (listRef.current) {
        const selectedItem = listRef.current.children[
          currentIndex
        ] as HTMLDivElement;
        if (window.innerWidth > 750) {
          const offsetTop = selectedItem.offsetTop;
          const containerTop = listRef.current.offsetTop;
          if (selectedItem) {
            listRef.current.scrollTo({
              behavior: 'smooth',
              top: offsetTop - containerTop - selectedItem.clientHeight - 40,
            });
          }
        } else {
          const offsetLeft = selectedItem.offsetLeft;
          const containerWidth = listRef.current.clientWidth;
          const selectedItemWidth = selectedItem.clientWidth;
          if (selectedItem) {
            listRef.current.scrollTo({
              behavior: 'smooth',
              left:
                offsetLeft - containerWidth / 2 + selectedItemWidth / 2 - 20,
            });
          }
        }
      }
    }, 50);
  }, [currentIndex]);
  const { isLoading, data, error } = useQuery<
    ApiResponse,
    Error,
    ApiResponse,
    string[]
  >({
    queryKey: ['homePageData'],
    queryFn: fetchHomePageData,
  });

  useEffect(() => {
    if (data) {
      const { domains } = data.data;
      setDomains(domains);
    }
  }, [data]);

  useEffect(() => {
    handleScroll();
  }, [currentIndex, handleScroll]);

  const renderProjectCard = useCallback((project: TopProject) => {
    return (
      <MotionProjectSliderCard>
        <PortfolioCard
          allocation={project.Team_Allocation}
          width="413px"
          arrowBackground={COLORS.PRODUCTS_ARROW}
          arrowBorder="none"
          projectId={project._id}
          expertises={project.Expertises.map(expertise => expertise.Name)}
          title={project.Name}
          category={project.Domain?.Name ? [project.Domain.Name] : []}
          caption={project.Caption ?? ''}
          technologies={project.Technologies.map(tech => tech.Name)}
          imageUrl={project.Image}
          initImageHeight="340px"
          imageHeight="340px"
          halfImageHeight="170px"
          imageWidth="100%"
          border="none"
          isPortfolioPage={false}
        />
      </MotionProjectSliderCard>
    );
  }, []);

  const domainsData = useMemo(() => {
    return domains.map(item => {
      return {
        image: item.Image,
        name: item.Name,
        projectName: item.ProjectName,
      };
    });
  }, [domains]);

  if (error) {
    return (
      <Box>
        <ErrorWrapper>
          <Text text={error.message} />
        </ErrorWrapper>
      </Box>
    );
  }

  if (!data || isLoading) {
    return <Loader />;
  }

  const {
    homeInformation,
    aboutHomeInformation,
    homeAboutTechnology,
    teamFacts,
    developmentProcesses,
    faq,
    topList,
  } = data.data;
  const shownTeamFacts = showAllTeamsFacts
    ? teamFacts.Facts
    : teamFacts.Facts.slice(0, TEAM_FACTS_SHOWN);
  const topProjects = topList?.Projects || [];
  const activityIcons = getActivityIcons();
  const icons = getIndustryIcons();
  const handleNext = () => {
    setCurrentIndex(prevState => Math.min(prevState + 1, domains.length - 1));
  };
  const handlePrevious = () => {
    setCurrentIndex(prevState => Math.max(prevState - 1, 0));
  };

  return (
    <>
      {homeInformation && (
        <HomeHeader>
          <VerticalBox>
            <Box>
              <HeaderWrapper>
                <TextWrapper>
                  <TextTitleWrapper>
                    {homeInformation.Title.split('/').map((item, index) => (
                      <Title
                        title={item}
                        key={index}
                        fontFamily={FONTS.TITLE}
                        size={60}
                        lineHeight={70}
                        weight={600}
                        color={COLORS.HEADER_TEXT_COLOR}
                      />
                    ))}
                  </TextTitleWrapper>
                  <Text
                    text={homeInformation.Information}
                    fontFamily={FONTS.SUBTITLE}
                    size={22}
                    lineHeight={28}
                    color={COLORS.HEADER_TEXT_COLOR}
                  />
                </TextWrapper>
                <HeaderLogo />
              </HeaderWrapper>
            </Box>
          </VerticalBox>
        </HomeHeader>
      )}

      <AboutWrapper>
        <VerticalBox>
          <Box>
            <About aboutInfo={aboutHomeInformation} />
          </Box>
        </VerticalBox>
      </AboutWrapper>

      <TechnologyWrapper>
        <VerticalBox>
          <Box>
            <Technology technologyInfo={homeAboutTechnology} />
          </Box>
        </VerticalBox>
      </TechnologyWrapper>

      <ActivitiesWrapper>
        <Box>
          <ActivitiesContent>
            <Title
              fontFamily={FONTS.TITLE}
              size={36}
              lineHeight={46}
              color={COLORS.SECTION_TITLE}
              title={teamFacts.Title}
              isAnimated={true}
            />
            <Text
              text={teamFacts.Description}
              fontFamily={FONTS.SUBTITLE}
              size={22}
              maxWidth={800}
              color={COLORS.SECTION_TEXT}
              isAnimated={true}
            />
            <ActivitiesContainer>
              {shownTeamFacts.map((fact, index) => (
                <ActivityCard
                  key={index}
                  icon={activityIcons[index]}
                  title={fact.Title}
                  subtitle={fact.Description}
                />
              ))}
            </ActivitiesContainer>
            {teamFacts.Facts.length > TEAM_FACTS_SHOWN && (
              <MotionScaleButton>
                <StyledButton
                  onClick={() => setShowAllTeamsFacts(prev => !prev)}
                >
                  {showAllTeamsFacts ? (
                    <>
                      <span>Hide all</span>
                      <UpIndustriesArrow />
                    </>
                  ) : (
                    <>
                      <span>Show more</span>
                      <DownIndustriesArrow />
                    </>
                  )}
                </StyledButton>
              </MotionScaleButton>
            )}
          </ActivitiesContent>
        </Box>
      </ActivitiesWrapper>
      <IndustriesWrapper>
        <Box>
          <IndustriesTitleWrapper>
            <Title title={t('screen.home.industries.title')} />
          </IndustriesTitleWrapper>
          <IndustriesContent>
            <IndustryGradientTop />
            <IndustryGradientBottom />
            {domains.length > 0 && (
              <IndustriesProgress
                currentIndex={currentIndex}
                count={domains.length}
                progressClick={(index: number) => setCurrentIndex(index)}
              />
            )}
            {domains.length > 0 && (
              <IndustriesSlider
                domains={domainsData}
                currentIndex={currentIndex}
                changeIndex={(index: number) => setCurrentIndex(index)}
              />
            )}

            <IndustriesList>
              {currentIndex > 0 ? (
                <ArrowContainer onClick={handlePrevious}>
                  <UpIndustriesArrow />
                </ArrowContainer>
              ) : (
                <ArrowEmpty />
              )}
              <IndustriesListContent ref={listRef}>
                {domains.map((domain, index) => {
                  return (
                    <IndustryCard
                      key={index}
                      icon={icons[domain?.Name.trim()] || icons.default}
                      title={domain?.Name}
                      subtitle={domain?.Caption}
                      isSelected={index === currentIndex}
                      onClick={() => {
                        setCurrentIndex(index);
                      }}
                      data-image-src={domain?.Image}
                    />
                  );
                })}
              </IndustriesListContent>
              {currentIndex < domains.length - 1 ? (
                <ArrowContainer onClick={handleNext}>
                  <DownIndustriesArrow />
                </ArrowContainer>
              ) : (
                <ArrowEmpty />
              )}
            </IndustriesList>
          </IndustriesContent>
          <MotionScaleButton>
            <StyledLinkButton to={ROUTES.INDUSTRIES}>
              <Text text={t('screen.home.industries.button')} />
              <ArrowRightSmall />
            </StyledLinkButton>
          </MotionScaleButton>
        </Box>
      </IndustriesWrapper>
      <DevelopmentWrapper>
        <VerticalBox>
          <Box>
            <Title
              title={t('screen.home.development.title')}
              isAnimated={true}
            />
            <DevelopmentContent>
              <StepsList>
                {developmentProcesses.map((process, index) => (
                  <StepItem
                    key={index}
                    onClick={() => setSelectedProcess(index)}
                    $isSelected={index === selectedProcess}
                  >
                    {process.Title}
                  </StepItem>
                ))}
              </StepsList>
              <DevContentBox selectedProcess={selectedProcess}>
                <DevTextBox>
                  <Text
                    text={developmentProcesses[selectedProcess].id.padStart(
                      2,
                      '0'
                    )}
                    size={22}
                    lineHeight={28}
                    color={COLORS.PRODUCTS_ARROW}
                  />
                  <Title
                    title={developmentProcesses[selectedProcess].Title}
                    size={24}
                    weight={600}
                    lineHeight={32.4}
                    minHeight={23}
                  />
                  <Text
                    text={developmentProcesses[selectedProcess].Description}
                    size={18}
                    lineHeight={27}
                  />
                  <KeyPointsList
                    list={developmentProcesses[selectedProcess].KeyPoints}
                  />
                </DevTextBox>
              </DevContentBox>
            </DevelopmentContent>
          </Box>
        </VerticalBox>
      </DevelopmentWrapper>

      <AllTechnologiesWrapper>
        <VerticalBox>
          <Box>
            <AllTechnologies />
          </Box>
        </VerticalBox>
      </AllTechnologiesWrapper>

      <CtaWrapper>
        <Box>
          <Cta />
        </Box>
      </CtaWrapper>

      <FAQsWrapper>
        <Box>
          <Faq faqs={faq} />
        </Box>
      </FAQsWrapper>
      <PortfolioWrapper>
        <VerticalBox>
          <Box>
            <PortfolioTitleWrapper>
              <Title
                title={t('screen.home.portfolio.title')}
                isAnimated={true}
              />
              <StyledLink to={ROUTES.PORTFOLIO}>View all</StyledLink>
            </PortfolioTitleWrapper>
          </Box>
          <PortfolioSlider data={topProjects} renderItem={renderProjectCard} />
        </VerticalBox>
      </PortfolioWrapper>
    </>
  );
};

const HomeHeader = styled(MotionMainHeaderBackground)``;

const HeaderWrapper = styled.div`
  display: flex;
  gap: 150px;
  @media screen and (${device.lg}) {
    gap: 100px;
  }
  @media screen and (${device.md}) {
    gap: 50px;
  }
  @media screen and (${device.sm}) {
    flex-direction: column-reverse;
    align-items: center;
    gap: 10px;
  }
`;
const TextTitleWrapper = styled.div`
  > span {
    display: block;
  }
`;
const TextWrapper = styled(MotionText)`
  display: flex;
  flex-direction: column;
  gap: 40px;
  @media screen and (${device.lg}) {
    span {
      font-size: 40px;
      line-height: 48px;
    }

    gap: 20px;
  }
  @media screen and (${device.md}) {
    span {
      font-size: 30px;
      line-height: 38px;
    }

    > h4 {
      font-size: 18px;
      line-height: 26px;
    }
  }
  @media screen and (${device.sm}) {
    > * {
      text-align: center;
    }

    span {
      font-size: 24px;
      line-height: 30px;
    }

    > h4 {
      font-size: 15px;
      line-height: 20px;
    }
  }
`;

const IndustriesTitleWrapper = styled(MotionText)``;

const AboutWrapper = styled.div``;

const TechnologyWrapper = styled.div`
  background-color: ${COLORS.GRAY_BG_COLOR};
`;

const ActivitiesWrapper = styled.div`
  background-color: ${COLORS.LIGHT_BG_COLOR};
  padding: 100px 0;
  @media screen and (${device.md}) {
    padding: 35px 0;
  }
`;

const ActivitiesContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 60px;
  justify-content: center;
  @media screen and (${device.md}) {
    gap: 30px;
    > span {
      font-size: 25px;
      line-height: 30px;
    }

    > h4 {
      font-size: 16px;
      line-height: 20px;
    }
  }
  @media screen and (${device.sm}) {
    gap: 20px;
  }
`;
const StyledButton = styled(Button)`
  width: 180px;
  background: transparent;
  border: 1px solid ${COLORS.STEP_RED_BORDER};
  svg {
    color: ${COLORS.SECTION_TEXT};
  }
  &:hover > span,
  &:hover svg {
    color: ${COLORS.RED_HOVER};
  }
  &:active {
    background: ${COLORS.RED_PRESSED};
    > span,
    svg {
      color: ${COLORS.WHITE};
    }
  }
  > span {
    display: flex;
    color: ${COLORS.SECTION_TITLE};
    align-items: center;
    gap: 10px;
  }
`;

const ActivitiesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 80px;
  @media screen and (${device.md}) {
    gap: 20px;
  }
`;

const IndustriesWrapper = styled.div`
  background-color: ${COLORS.GRAY_BG_COLOR};
  position: relative;
  padding: 87px 0 93px 0;
  @media screen and (${device.md}) {
    padding: 35px 0;
    > div > span {
      font-size: 25px;
    }
  }
`;
const IndustryGradientTop = styled.div`
  position: absolute;
  top: 30px;
  background: linear-gradient(
    180deg,
    ${COLORS.GRAY_BG_COLOR} 0%,
    rgba(245, 245, 247, 0) 50.67%
  );
  width: 100%;
  pointer-events: none;
  height: 150px;
  z-index: 2;
  @media screen and (${device.bsm}) {
    display: none;
  }
`;
const IndustryGradientBottom = styled.div`
  position: absolute;
  bottom: 30px;
  background: linear-gradient(
    180deg,
    rgba(245, 245, 247, 0) 50.67%,
    ${COLORS.GRAY_BG_COLOR} 100%
  );
  width: 100%;
  pointer-events: none;
  height: 150px;
  z-index: 2;
  @media screen and (${device.bsm}) {
    display: none;
  }
`;
const IndustriesContent = styled.div`
  display: flex;
  margin-top: 60px;
  gap: 30px;
  justify-content: space-between;
  align-items: center;
  position: relative;
  @media screen and (${device.bsm}) {
    flex-direction: column;
    margin-top: 20px;
  }
`;

const IndustriesList = styled.div`
  display: flex;
  flex-direction: column;
  width: 610px;
  user-select: none;
  position: relative;
  @media screen and (${device.bsm}) {
    order: 3;
    flex-direction: row;
    align-items: center;
    width: 100%;
    height: 200px;
  }
  @media screen and (${device.sm}) {
    height: 150px;
  }
`;
const IndustriesListContent = styled.div`
  height: 710px;
  display: flex;
  flex-direction: column;
  gap: 40px;
  align-items: center;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  scroll-snap-type: none;
  @media screen and (${device.xl}) and (${deviceHeight.md}) {
    height: 530px;
  }
  @media screen and (${device.bsm}) {
    flex-direction: row;
    height: 200px;
    width: 100%;
  }
  @media screen and (${device.sm}) {
    height: 150px;
  }
`;

const DevelopmentWrapper = styled.div`
  background-color: ${COLORS.LIGHT_BG_COLOR};

  > div > div > span {
    display: block;
    margin-bottom: 60px;
  }

  @media screen and (${device.md}) {
    > div > div > span {
      margin-bottom: 20px;
      font-size: 25px;
      line-height: 30px;
    }
  }
  @media screen and (${device.sm}) {
    > div > div > span {
      margin-bottom: 5px;
      font-size: 22px;
      line-height: 26px;
    }
  }
`;

const FAQsWrapper = styled.div`
  background-color: ${COLORS.LIGHT_BG_COLOR};
  padding: 80px 0;

  > div > div {
    margin-top: 60px;
  }

  @media screen and (${device.md}) {
    padding: 20px 0;
    > div > div {
      margin-top: 0;
    }

    > div > span {
      font-size: 20px;
      line-height: 26px;
    }
  }
`;

const DevelopmentContent = styled.div`
  display: flex;
  gap: 80px;
  @media screen and (${device.md}) {
    gap: 20px;
  }
`;

const StepsList = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 28px;
  width: 320px;
  @media screen and (${device.md}) {
    width: auto;
    padding-left: 0;
  }
`;

const StepItem = styled.div<{ $isSelected: boolean }>`
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  line-height: 20.16px;
  font-family: ${FONTS.TITLE};
  &:hover {
    color: ${COLORS.STEP_ACTIVE_COLOR};
  }
  color: ${({ $isSelected }) =>
    $isSelected ? COLORS.STEP_ACTIVE_COLOR : COLORS.STEP_INACTIVE_COLOR};
  border-left: ${({ $isSelected }) =>
    $isSelected
      ? `3px solid ${COLORS.STEP_RED_BORDER}`
      : `1px solid ${COLORS.STEP_GRAY_BORDER}`};
  height: 52px;
  width: 100%;
  padding: 16px 12px 16px 24px;
  box-sizing: border-box;
  @media screen and (${device.md}) {
    font-size: 12px;
    line-height: 18px;
  }
  @media screen and (${device.sm}) {
    font-size: 10px;
    line-height: 15px;
    height: auto;
    padding: 7px;
    max-width: 100px;
  }
`;

const DevContentBox = styled(MotionDevelopmentProcess)`
  display: flex;
  flex-direction: column;
  flex: 1;
  background-color: ${COLORS.GRAY_BG_COLOR};
  padding: 40px;
  @media screen and (${device.md}) {
    padding: 20px;
    justify-content: flex-start;
  }
  @media screen and (${device.sm}) {
    padding: 10px;
    justify-content: flex-start;
  }
`;

const DevTextBox = styled.div`
  max-width: 74.5%;

  > h4:nth-child(1) {
    margin-bottom: 10px;
  }

  > h4:nth-child(3) {
    margin-top: 40px;
  }

  @media screen and (${device.lg}) {
    max-width: none;
  }
  @media screen and (${device.md}) {
    max-width: 100%;
  }
  @media screen and (${device.md}) {
    > h4:nth-child(1) {
      margin-bottom: 10px;
      font-size: 20px;
    }

    > span {
      font-size: 18px;
      line-height: 22px;
    }

    > h4:nth-child(3) {
      margin-top: 10px;
      font-size: 14px;
      line-height: 18px;
    }
  }
  @media screen and (${device.sm}) {
    > h4:nth-child(1) {
      margin-bottom: 5px;
      font-size: 15px;
    }

    > span {
      font-size: 14px;
      line-height: 20px;
    }

    > h4:nth-child(3) {
      margin-top: 3px;
      font-size: 12px;
      line-height: 14px;
    }
  }
`;

const AllTechnologiesWrapper = styled.div`
  background: ${COLORS.GRAY_BG_COLOR};
`;
const CtaWrapper = styled.div`
  background-color: ${COLORS.CTA_BACKGROUND};
  overflow: hidden;
`;
const PortfolioWrapper = styled.div`
  background-color: ${COLORS.GRAY_BG_COLOR};

  > div > span {
    font-size: 20px;
    line-height: 26px;
  }
`;

const ErrorWrapper = styled.div`
  padding: 20px 0;
`;
const ArrowContainer = styled.div`
  box-sizing: border-box;
  cursor: pointer;
  padding: 10px 0;
  display: flex;
  justify-content: center;
  @media screen and (${device.bsm}) {
    width: 20px;
    padding: 0;
    flex-shrink: 0;
    svg {
      transform: rotate(-90deg);
    }

    height: 100%;
    display: flex;
    align-items: center;
  }
`;
const ArrowEmpty = styled.div`
  height: 30px;
  width: 100%;
  @media screen and (${device.bsm}) {
    width: 20px;
    height: 100%;

    flex-shrink: 0;
  }
`;
const PortfolioTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  @media screen and (${device.md}) {
    > span {
      font-size: 25px;
      line-height: 30px;
      min-height: 0;
    }
  }
  @media screen and (${device.sm}) {
    > span {
      font-size: 20px;
      line-height: 25px;
    }
  }
`;
const StyledLinkButton = styled(Link)`
  height: 55px;
  width: 233px;
  display: flex;
  justify-content: center;
  gap: 20px;
  align-items: center;
  margin: 60px auto 0;
  background: transparent;
  border: 1px solid ${COLORS.STEP_RED_BORDER};
  text-decoration: none;
  svg {
    color: ${COLORS.SECTION_TEXT};
  }
  &:hover > h4,
  &:hover svg {
    color: ${COLORS.RED_HOVER};
  }
  &:active {
    background: ${COLORS.RED_PRESSED};
    > h4,
    svg {
      color: ${COLORS.WHITE};
    }
  }
  h4 {
    color: ${COLORS.SECTION_TITLE};
    font-size: 18px;
    line-height: 27px;
  }
`;
const StyledLink = styled(Link)`
  color: ${COLORS.SECTION_TITLE};
  text-decoration: none;
  font-size: 24px;
  font-weight: 600;
  @media screen and (${device.sm}) {
    font-size: 22px;
    line-height: 26px;
  }
`;
